/* eslint-disable react/no-unknown-property */
import { OrbitControls, OrbitControlsProps } from "@react-three/drei";
import { ThreeEvent, useLoader } from "@react-three/fiber";
import { animate } from "framer-motion";
import React, {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useIdle } from "react-use";
import { TextureLoader, Vector3 } from "three";

import {
  getMarkerTransformOriginAngle,
  getPositionFromCoordinates,
} from "../../../utils/world-map";
import { cameraDistance, markerHeight, markerWidth } from "./constants";
import { Marker } from "./marker";
import EarthTexture from "./textures/world-4.jpg";

export interface EarthProps {
  radius?: number;
  rotationSpeed?: number;
  markers?: WorldMapMarker[];
  onClickMarker?: (id: string) => void;
  isRotationEnabled: boolean;
  setRotationEnabled: Dispatch<SetStateAction<boolean>>;
  selectedMarkerId?: string;
  reactivateRotationAfterMs?: number;
}

export interface WorldMapMarker {
  id: string;
  lat: number;
  lng: number;
}

export interface EarthControls {
  moveCameraToPoint: (position: Vector3) => void;
  moveCameraToMarker: (id: string) => void;
}

export const Earth = forwardRef<EarthControls, EarthProps>(
  (
    {
      radius = 3,
      rotationSpeed = 2,
      markers = [],
      onClickMarker: _onClickMarker,
      isRotationEnabled,
      setRotationEnabled,
      selectedMarkerId,
      reactivateRotationAfterMs,
    },
    ref
  ) => {
    const [textureMap] = useLoader(TextureLoader, [EarthTexture]);
    const orbitControlsRef = useRef<OrbitControlsProps>(null);

    const isIdle = useIdle(reactivateRotationAfterMs);
    useEffect(() => {
      if (!selectedMarkerId && isIdle && reactivateRotationAfterMs !== undefined) {
        setRotationEnabled(true);
      }
    }, [selectedMarkerId, isIdle, setRotationEnabled, reactivateRotationAfterMs]);

    const onPointerUp = useCallback(() => {
      setRotationEnabled(false);
    }, [setRotationEnabled]);

    const pinsPositions = useMemo(() => {
      return markers.map(({ lat, lng, id }) => {
        // unselected markers don't need offset (they're circles so it's ok for them to
        // have origin in center).
        // selected markers need offset because they must have origin in bottom.
        const offset =
          selectedMarkerId === id ? getMarkerTransformOriginAngle(markerHeight, radius) : 0;

        return {
          position: getPositionFromCoordinates(lat + offset, lng, radius),
          id,
        };
      });
    }, [radius, markers, selectedMarkerId]);

    const moveCameraToPoint = useCallback(
      (position: Vector3) => {
        const camera = orbitControlsRef.current;

        if (camera?.object) {
          setRotationEnabled(false);

          const steps = 100;
          animate(0, steps, {
            duration: 1,
            ease: "easeInOut",
            onUpdate: (latest) => {
              camera.object?.position.lerp(
                new Vector3(
                  (position.x * cameraDistance) / radius,
                  (position.y * cameraDistance) / radius,
                  (position.z * cameraDistance) / radius
                ),
                latest / steps
              );
            },
          });
          camera.object.lookAt(position);
        }
      },
      [radius, setRotationEnabled]
    );

    const onClickMarker = useCallback(
      (e: ThreeEvent<MouseEvent>, id: string) => {
        e.stopPropagation();
        _onClickMarker?.(id);
      },
      [_onClickMarker]
    );

    const moveCameraToMarker = useCallback(
      (markerId: string) => {
        const pin = pinsPositions.find(({ id }) => id === markerId);
        if (pin) {
          moveCameraToPoint(pin.position);
        }
      },
      [pinsPositions, moveCameraToPoint]
    );

    useImperativeHandle(ref, () => ({
      moveCameraToPoint,
      moveCameraToMarker,
    }));

    return (
      <>
        <ambientLight intensity={2.2} />

        <group>
          <mesh onPointerUp={onPointerUp} position={[0, 0, 0]}>
            <sphereGeometry args={[radius, 48, 48]} />
            <meshStandardMaterial map={textureMap} />
          </mesh>

          {pinsPositions.map(({ position, id }, i) => (
            <Marker
              position={position}
              key={i}
              width={markerWidth}
              height={markerHeight}
              isSelected={selectedMarkerId === id}
              onPointerUp={(e) => onClickMarker(e, id)}
            />
          ))}
        </group>

        <OrbitControls //
          ref={orbitControlsRef as any}
          minPolarAngle={Math.PI / 3}
          maxPolarAngle={(Math.PI / 3) * 2}
          maxDistance={cameraDistance}
          minDistance={cameraDistance}
          enableRotate
          enablePan={false}
          enableZoom={false}
          autoRotate={isRotationEnabled}
          autoRotateSpeed={isRotationEnabled ? rotationSpeed : 0}
        />
      </>
    );
  }
);
